import { AiImageStudioConfig, AiImageStudioModelConfig } from './types';

export const OPENAI_FLARE_MODEL_ID = 'gpt-image-2.5-flare';
export const OPENAI_SUNBURST_MODEL_ID = 'gpt-image-2.5-sunburst';

const RETIRED_OPENAI_MODELS = new Set([
  'gpt-image-2',
  'gpt-image-1',
  'gpt-image-1-mini',
  'gpt-image-1.5',
  'chatgpt-image-latest',
]);

const MODEL_LABELS: Record<string, string> = {
  'gemini-3.1-flash-image': 'Nano Banana 2 / Gemini 3.1 Flash Image',
  'gemini-3-pro-image': 'Nano Banana Pro / Gemini 3 Pro Image',
  'gpt-image-2.5-flare': 'GPT Image 2.5 Flare (snabb)',
  'gpt-image-2.5-sunburst': 'GPT Image 2.5 Sunburst (precision)',
  'gpt-image-2': 'GPT Image 2 / OpenAI',
  'gpt-image-1': 'GPT Image 1 / OpenAI',
  'gpt-image-1-mini': 'GPT Image 1 Mini / OpenAI',
  'gpt-image-1.5': 'GPT Image 1.5 / OpenAI',
  'chatgpt-image-latest': 'ChatGPT Image / OpenAI',
};

export const IMAGE_SIZE_HELP =
  '1K är ca 1024 px, 2K ca 2048 px och 4K upp till 3840 px på långsidan (kvadrat ca 2880 px på grund av API-taket). Ratio Auto ger en kvadrat i vald storleksklass. Faktiska pixlar syns i detaljer.';

export const IMAGE_QUALITY_HELP =
  'Low = snabb skiss. Medium = vardag. High = slutresultat. Extra high och Max = mer detalj, långsammare och dyrare. Auto = modellen väljer.';

export function getAiImageModelLabel(modelId?: string): string {
  if (!modelId) return '';
  return MODEL_LABELS[modelId] || modelId;
}

export function isRetiredOpenAiImageModel(modelId?: string): boolean {
  return Boolean(modelId && RETIRED_OPENAI_MODELS.has(modelId));
}

const FALLBACK_OPENAI_MODELS: AiImageStudioModelConfig[] = [
  {
    id: OPENAI_FLARE_MODEL_ID,
    label: 'GPT Image 2.5 Flare (snabb)',
    provider: 'openai',
    maxReferences: 14,
    supportsReferences: true,
    outputFormats: ['jpeg', 'png', 'webp'],
    supportsTransparentBackground: true,
  },
  {
    id: OPENAI_SUNBURST_MODEL_ID,
    label: 'GPT Image 2.5 Sunburst (precision)',
    provider: 'openai',
    maxReferences: 14,
    supportsReferences: true,
    outputFormats: ['jpeg', 'png', 'webp'],
    supportsTransparentBackground: true,
  },
];

export function getSelectableStudioModels(
  config: AiImageStudioConfig | null | undefined,
): AiImageStudioModelConfig[] {
  const fromConfig = (config?.models || []).filter((model) => !isRetiredOpenAiImageModel(model.id));
  if (!config) return fromConfig;
  const extras = FALLBACK_OPENAI_MODELS.filter(
    (fallback) => !fromConfig.some((model) => model.id === fallback.id),
  );
  return [...fromConfig, ...extras];
}

export function getSelectableOpenAiModel(
  config: AiImageStudioConfig | null | undefined,
): AiImageStudioModelConfig | null {
  return getSelectableStudioModels(config).find((model) => model.provider === 'openai') || null;
}

export function resolveSelectableModelId(
  modelId: string | undefined,
  config: AiImageStudioConfig | null | undefined,
): string {
  const selectableModels = getSelectableStudioModels(config);
  if (isRetiredOpenAiImageModel(modelId)) {
    return selectableModels.find((model) => model.provider === 'openai')?.id || OPENAI_FLARE_MODEL_ID;
  }
  if (modelId && selectableModels.some((model) => model.id === modelId)) {
    return modelId;
  }
  return config?.defaultModel || selectableModels[0]?.id || modelId || '';
}

export function formatOpenAiQualityLabel(quality: string): string {
  if (quality === 'auto') return 'Auto';
  if (quality === 'xhigh') return 'Extra high';
  if (quality === 'max') return 'Max';
  return quality.charAt(0).toUpperCase() + quality.slice(1);
}

export function formatImagePixelLabel(width?: number, height?: number): string {
  if (!width || !height) return '';
  return `${width} × ${height} px`;
}
